/**
 * User Resource
 *
 * Per-user preferences that follow the authenticated user across all
 * workspaces and sessions. Thin 1:1 mapping to the /v1/user API. The
 * user identity is taken from the JWT claims server-side — the client never
 * passes a user id.
 *
 * Profile read/write lives on `sdk.context.profile` (same `/v1/user/profile`
 * endpoint) — the dead `getProfile`/`updateProfile` aliases were removed.
 */

import type { HttpLayer } from '../http.js';
import type { UserPreferences, UserUiPreferences } from '../types/index.js';

export class UserResource {
  constructor(private http: HttpLayer) {}

  /** Read the authenticated user's preferences. Always resolves to an object. */
  async getPreferences(): Promise<UserPreferences> {
    return this.http.get('/v1/user/preferences');
  }

  /**
   * Shallow-merge a partial preferences patch into the stored blob. Top-level
   * keys in `patch` overwrite; keys not present are preserved. Returns the
   * merged preferences.
   */
  async updatePreferences(
    patch: Partial<UserPreferences>,
  ): Promise<UserPreferences> {
    return this.http.patch('/v1/user/preferences', patch);
  }

  async getUiPreferences(): Promise<UserUiPreferences> {
    return this.http.get('/v1/user/ui-preferences');
  }

  async updateUiPreferences(
    patch: UserUiPreferences,
  ): Promise<UserUiPreferences> {
    return this.http.patch('/v1/user/ui-preferences', patch);
  }
}
